// Game invitation modal - shows pending game invitations as a centered modal

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { siteEvents } from '../../lib/site-events.js';

class GameInvitationBanner extends BaseComponent {
  static properties = {
    _invitations: { type: Array, state: true },
    _dismissed: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 2000;
      padding: 1rem;
    }

    .modal-panel {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
      width: 100%;
      max-width: 380px;
      overflow: hidden;
      text-align: center;
    }

    .modal-body {
      padding: 1.75rem 1.5rem 1.5rem;
    }

    .invite-icon {
      color: #0d6efd;
      margin-bottom: 0.75rem;
    }

    .invite-heading {
      font-size: 1.15rem;
      font-weight: 600;
      color: #212529;
      margin: 0 0 0.25rem;
    }

    .invite-adventure {
      font-size: 1rem;
      color: #495057;
      margin: 0 0 1.25rem;
    }

    .join-btn {
      display: block;
      width: 100%;
      padding: 0.7rem;
      font-size: 0.95rem;
      font-weight: 600;
      color: #ffffff;
      background: #0d6efd;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .join-btn:active {
      background: #0b5ed7;
    }

    .modal-footer {
      padding: 0 1.5rem 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .other-invitations {
      font-size: 0.85rem;
      color: #0d6efd;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      -webkit-tap-highlight-color: transparent;
    }

    .other-invitations:hover {
      text-decoration: underline;
    }

    .dismiss-btn {
      font-size: 0.85rem;
      color: #6c757d;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0.25rem;
      -webkit-tap-highlight-color: transparent;
    }
  `;

  constructor() {
    super();
    this._invitations = [];
    this._dismissed = false;
    /** @type {Set<string>} Game IDs of invitations the user has seen and acted on */
    this._seenInvitationIds = new Set(JSON.parse(localStorage.getItem('rpgish-seen-invitations') || '[]'));
    /** @type {(() => void)|null} */
    this._unsubscribeSite = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    // Initial fetch on mount so invitations that arrived before we
    // subscribed are visible. After that, the site SSE stream pokes us
    // when a new invitation lands and we re-fetch.
    this._fetchInvitations();
    this._unsubscribeSite = siteEvents.subscribe('game-invited', () => {
      this._fetchInvitations();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._unsubscribeSite) {
      this._unsubscribeSite();
      this._unsubscribeSite = null;
    }
  }

  async _fetchInvitations() {
    try {
      const allInvitations = await api.getMyInvitations();
      // Filter out invitations the user has already seen and acted on
      const unseen = allInvitations.filter((inv) => !this._seenInvitationIds.has(inv.gameId));
      if (unseen.length > this._invitations.length) {
        this._dismissed = false;
      }
      this._invitations = unseen;
    } catch {
      // Silently ignore
    }
  }

  /** Mark all currently shown invitations as seen */
  _markCurrentAsSeen() {
    for (const inv of this._invitations) {
      this._seenInvitationIds.add(inv.gameId);
    }
    try {
      localStorage.setItem('rpgish-seen-invitations', JSON.stringify([...this._seenInvitationIds]));
    } catch {
      // localStorage full — ignore
    }
  }

  _dismiss() {
    this._markCurrentAsSeen();
    this._dismissed = true;
  }

  /** @param {string} gameId */
  _joinGame(gameId) {
    this._markCurrentAsSeen();
    this._dismissed = true;
    router.navigate(`/game/${gameId}/join`);
  }

  _viewAllInvitations() {
    this._markCurrentAsSeen();
    this._dismissed = true;
    router.navigate('/account?tab=invitations');
  }

  render() {
    if (!this._invitations.length || this._dismissed) return '';

    const latest = this._invitations[0];
    const otherCount = this._invitations.length - 1;

    return html`
      <div class="modal-backdrop" @click="${this._dismiss}">
        <div class="modal-panel" @click="${(/** @type {Event} */ e) => e.stopPropagation()}">
          <div class="modal-body">
            <div class="invite-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
                <line x1="13" x2="19" y1="19" y2="13" />
                <line x1="16" x2="20" y1="16" y2="20" />
                <line x1="19" x2="21" y1="21" y2="19" />
                <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
                <line x1="5" x2="9" y1="14" y2="18" />
                <line x1="7" x2="4" y1="17" y2="20" />
                <line x1="3" x2="5" y1="19" y2="21" />
              </svg>
            </div>
            <p class="invite-heading">${latest.inviterUsername} has invited you to play!</p>
            <p class="invite-adventure">${latest.gameAdventureName}</p>
            <button class="join-btn" @click="${() => this._joinGame(latest.gameId)}">Join Game</button>
          </div>
          <div class="modal-footer">
            ${
              otherCount > 0
                ? html`
              <button class="other-invitations" @click="${this._viewAllInvitations}">
                You have ${otherCount} other invitation${otherCount > 1 ? 's' : ''}
              </button>
            `
                : ''
            }
            <button class="dismiss-btn" @click="${this._dismiss}">Not now</button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('game-invitation-banner', GameInvitationBanner);
